import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    styled,
    Typography,
    Grid,
    Card,
    CardContent,
    Tooltip
} from '@mui/material';

// Earned leave accrues on a working-day count rather than a yearly grant, and
// what is shown is last year's closing balance — neither is obvious from the
// bare figure, so the card explains itself on hover.
const EARNED_LEAVE_NOTE =
    'Earned leave is calculated for every 18 working days. The balance shown here is from last year.';

const isEarnedLeave = (name = '') => name.toLowerCase().includes('earn');

const StyledCard = styled(Card)(({ theme: { spacing, palette } }) => ({
    minWidth: '200px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    textAlign: 'left',
    gap: spacing(1),
    border: `1px solid ${palette.divider}`,
    boxShadow: 'none'
}));

const StyledBox = styled(Box)(({ theme: { spacing } }) => ({
    padding: spacing(2),
}));

/**
 * Shared component for displaying leave data in card format
 * Used by both /leave-details and /leave-&-holidays/all-leaves pages
 */
const LeavesCard = ({ data = [], ...props }) => {
    const sortedData = data
        .filter((item) => item?.totalDays !== -1)
        .sort((a, b) => {
            const priority = ['casual', 'sick', 'earn'];
            const aName = a.category.name.toLowerCase();
            const bName = b.category.name.toLowerCase();

            const aIndex = priority.findIndex((key) =>
                aName.includes(key)
            );
            const bIndex = priority.findIndex((key) =>
                bName.includes(key)
            );

            if (aIndex !== -1 && bIndex !== -1)
                return aIndex - bIndex;

            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;

            return 0;
        });

    return (
        <StyledBox>
            <Grid container spacing={2} sx={{ margin: 'unset !important', gap: 2 }}>
                {sortedData.map((item, index) => (
                    <Grid item sx={{ padding: 'unset !important' }} key={item?.category?.name}>
                        <Tooltip
                            title={isEarnedLeave(item?.category?.name) ? EARNED_LEAVE_NOTE : ''}
                            arrow
                            placement="top"
                            enterDelay={0}
                            enterTouchDelay={0}
                        >
                            <StyledCard>
                                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 }, width: '100%' }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontSize: '0.875rem',
                                            mb: 1,
                                            lineHeight: 1.2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        {item?.category?.name}
                                        {isEarnedLeave(item?.category?.name) && (
                                            <InfoOutlinedIcon
                                                sx={{ fontSize: 14, color: 'text.secondary' }}
                                            />
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontSize: '0.875rem',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {item.totalDays - item.remainingDays}/{item.totalDays} Days
                                    </Typography>
                                </CardContent>
                            </StyledCard>
                        </Tooltip>
                    </Grid>
                ))}
            </Grid>
        </StyledBox>
    );
};

export default LeavesCard;
